import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useRateCardsActions } from "tap/store/rateCards/useRateCards.js";
import { tapApi } from "tap/api.js";
import { getTierName } from "../utils/payloadBuilder";
import {
  getTapWindowIdForPriceColumn,
  normalizePriceInput,
  sortRateCardWindows,
} from "../utils/validation";

/**
 * Custom hook for managing rate card form operations in CREATE mode.
 * @returns {Object} - Form state and handlers for create mode
 */
export function useCreateRateCardForm() {
  const {
    currencyOptions,
    formatOptions,
    windowSequenceOptions,
    priceTypeOptions,
    revShareOptions,
    rateCardForm,
  } = useSelector((state) => state.rateCards);

  const { updateRateCardForm } = useRateCardsActions();

  const formData = rateCardForm;
  const stateKey = "rateCardForm";

  const tierFormats = useMemo(
    () => formData?.tierAliases || [],
    [formData?.tierAliases],
  );

  const handleInputChange = useCallback(
    async (field, value) => {
      if (field.startsWith("percentage_")) {
        const tapWindowId = field.split("_")[1];
        const updatedWindows = (formData.windows || []).map((win) =>
          String(win.id.tapWindowId) === String(tapWindowId)
            ? { ...win, percentage: value }
            : win,
        );
        updateRateCardForm({
          key: stateKey,
          data: { windows: updatedWindows },
        });
        return;
      }

      if (field === "windowSequence") {
        const windowSequenceId = value?.value;
        const data = await tapApi.windowSequenceById(windowSequenceId);
        const windows = Array.isArray(data.windows)
          ? data.windows.map((w) => ({
              id: { tapWindowId: w.id },
              window: { ...w },
              percentage: "",
            }))
          : [];

        updateRateCardForm({
          key: stateKey,
          data: { tierRows: [], windows, [field]: value },
        });
        return;
      }

      if (field === "priceType") {
        updateRateCardForm({
          key: stateKey,
          data: { tierRows: [], [field]: value },
        });
        return;
      }

      updateRateCardForm({
        key: stateKey,
        data: { [field]: value },
      });
    },
    [formData, updateRateCardForm],
  );

  const handleAddTierFormat = useCallback(() => {
    const tierRows = formData?.tierRows || [];
    const newIdx = tierFormats.length;
    const newAlias = {
      index: newIdx,
      sortOrder: newIdx + 1,
      tierAliasName: "",
      tierAliasFormat: null,
    };
    const updatedAliases = [...tierFormats, newAlias];

    const updatedTierRows = tierRows.map((row, rowIdx) => {
      const tierName = getTierName(rowIdx, updatedAliases.length - 1);
      return {
        ...row,
        aliasColumns: [...(row.aliasColumns || []), { tierName }],
      };
    });

    updateRateCardForm({
      key: stateKey,
      data: { tierAliases: updatedAliases, tierRows: updatedTierRows },
    });
  }, [formData, tierFormats, updateRateCardForm]);

  const handleRemoveTierFormat = useCallback(
    (idx) => {
      const tierRows = formData?.tierRows || [];
      const updatedAliases = tierFormats.filter((_, i) => i !== idx);
      const updatedTierRows = tierRows.map((row) => ({
        ...row,
        aliasColumns: (row.aliasColumns || []).filter((_, i) => i !== idx),
      }));
      updateRateCardForm({
        key: stateKey,
        data: { tierAliases: updatedAliases, tierRows: updatedTierRows },
      });
    },
    [formData, tierFormats, updateRateCardForm],
  );

  const handleTierFormatChange = useCallback(
    (idx, field, value) => {
      const updatedAliases = tierFormats.map((t, i) =>
        i === idx ? { ...t, [field]: value } : t,
      );
      updateRateCardForm({
        key: stateKey,
        data: { tierAliases: updatedAliases },
      });
    },
    [tierFormats, updateRateCardForm],
  );

  const handleAddTierRow = useCallback(() => {
    const tierRows = formData?.tierRows || [];
    const newRowIdx = tierRows.length;
    const aliasColumns = (formData.tierAliases || []).map((_, aliasIdx) => ({
      tierName: getTierName(newRowIdx, aliasIdx),
    }));
    const priceColumns = sortRateCardWindows(formData.windows || []).map(
      (window) => ({
        tapWindowId: window.id.tapWindowId,
        price: "",
      }),
    );
    const newRow = {
      tierNumber: newRowIdx + 1,
      aliasColumns,
      priceColumns,
    };
    updateRateCardForm({
      key: stateKey,
      data: { tierRows: [...tierRows, newRow] },
    });
  }, [formData, updateRateCardForm]);

  const handleRemoveTierRow = useCallback(
    (rowIdx) => {
      const tierRows = formData?.tierRows || [];
      const updatedRows = tierRows.filter((_, idx) => idx !== rowIdx);
      updateRateCardForm({
        key: stateKey,
        data: { tierRows: updatedRows },
      });
    },
    [formData, updateRateCardForm],
  );

  const handleTierRowInputChange = useCallback(
    (rowIdx, colType, colIdx, value) => {
      const tierRows = formData?.tierRows || [];
      const nextValue =
        colType === "price" ? normalizePriceInput(value) : value;
      const newRows = tierRows.map((row, i) => {
        if (i !== rowIdx) return row;
        if (colType === "alias") {
          const updatedAliasColumns = row.aliasColumns.map((col, idx) =>
            idx === colIdx ? { ...col, tierName: nextValue } : col,
          );
          return { ...row, aliasColumns: updatedAliasColumns };
        } else if (colType === "price") {
          const targetTapWindowId = getTapWindowIdForPriceColumn(
            formData?.windows,
            colIdx,
          );
          const updatedPriceColumns = row.priceColumns.map((col) =>
            String(col.tapWindowId) === String(targetTapWindowId)
              ? { ...col, price: nextValue }
              : col,
          );
          return { ...row, priceColumns: updatedPriceColumns };
        }
        return row;
      });
      updateRateCardForm({
        key: stateKey,
        data: { tierRows: newRows },
      });
    },
    [formData, updateRateCardForm],
  );

  const showTierFormat = useMemo(() => tierFormats.length > 0, [tierFormats]);
  const priceType = useMemo(
    () => formData?.priceType?.value || "",
    [formData?.priceType?.value],
  );

  return {
    // Form data and options
    formData,
    tierFormats,
    currencyOptions,
    formatOptions,
    windowSequenceOptions,
    priceTypeOptions,
    revShareOptions,

    // Handlers
    handleInputChange,
    handleAddTierFormat,
    handleRemoveTierFormat,
    handleTierFormatChange,
    handleAddTierRow,
    handleRemoveTierRow,
    handleTierRowInputChange,

    // Computed values
    showTierFormat,
    priceType,
  };
}
